import { sipExtensionProvisioningSchema, sipExtensionRotationSchema, type SipExtensionRotationDto, type SipExtensionListItem as SipExtensionApiItem, type SipExtensionDndBody, type SipExtensionQueueResponse, type VoipActiveCallListItem, type SipExtensionProvisioningDto as SipCredentials } from '@easyflow/shared';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
    Phone,
    PhoneOff,
    Search,
    LayoutGrid,
    List as ListIcon,
    Headphones,
    Mic,
    MicOff,
    Users,
    PhoneForwarded,
    Pause,
    Play,
    Eye,
    EyeOff,
    Clock,
    ArrowDownLeft,
    ArrowUpRight,
    AlertCircle,
    RefreshCw,
    PhoneIncoming,
    PhoneOutgoing,
    Moon,
    LogIn,
    LogOut,
    Hash,
    Grip,
    X,
    Check,
    ChevronDown,
    Settings,
    UserCheck,
    UserX,
    PhoneMissed,
    Minimize2,
    Maximize2,
    Move,
    Copy,
    Smartphone,
    Key,
    RefreshCcw,
    Loader2
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@easyflow/ui-frontend/components/ui/card";
import { Badge } from "@easyflow/ui-frontend/components/ui/badge";
import { Button } from "@easyflow/ui-frontend/components/ui/button";
import { Avatar, AvatarFallback } from "@easyflow/ui-frontend/components/ui/avatar";
import { Input } from "@easyflow/ui-frontend/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@easyflow/ui-frontend/components/ui/select";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@easyflow/ui-frontend/components/ui/dropdown-menu";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@easyflow/ui-frontend/components/ui/alert-dialog";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from "@easyflow/ui-frontend/components/ui/dialog";
import { ScrollArea } from "@easyflow/ui-frontend/components/ui/scroll-area";
import { Separator } from "@easyflow/ui-frontend/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@easyflow/ui-frontend/components/ui/tooltip";
import { AppPageHeader } from "@/components/common/AppPageHeader";
import { useAuth } from '@/contexts/AuthContext';
import { useSocket } from '@/contexts/SocketContext';
import { apiClient } from '@/lib/api';
import { startSupervisorCall } from '@/lib/supervisor-preflight';
import { decideSwitchboardGating } from '@/lib/switchboard-gating';
import { usePermissions } from '@/contexts/PermissionContext';
import { useSipPhone } from '@/contexts/SipPhoneContext';
import { getErrorMessage } from '@/lib/utils';
import { getSipExtensionDndFeedback } from '../../components/sip-extension-dnd-feedback';
import { getSipExtensionQueueFeedback } from '../../components/sip-extension-queue-feedback';


interface ExtensionStatus {
    id?: string;
    extension: string;
    name: string;
    status: 'available' | 'busy' | 'ringing' | 'dnd' | 'offline' | 'away';
    activeCall?: ActiveCall;
    department?: string;
    is_registered?: boolean;
    queue_logged_in?: boolean;
    dnd_enabled?: boolean;
    queue_agent_status?: 'Available' | 'On Break' | 'Logged Out';
}

// Payload dei gruppi estensioni (GET /extension-groups).
interface ExtensionGroupApiItem {
    id: string;
    name: string;
    color?: string;
}

// Payload membro gruppo (GET /extension-groups/:id/members).
interface ExtensionGroupMemberApiItem {
    extension: string;
}

// Payload broadcast Socket.IO sullo stato/aggiornamento estensioni e agent/queue.
interface SwitchboardActionResultEvent {
    success?: boolean;
    message?: string;
    error?: string;
    action?: string;
}

interface ExtensionStatusChangeEvent {
    extension?: string;
    extension_id?: string;
    is_registered?: boolean;
}

interface ExtensionUpdateEvent {
    extension?: string;
    dnd_enabled?: boolean;
    queue_logged_in?: boolean;
}

interface QueueAgentStatusEvent {
    extensionNumber?: string;
    status?: 'Available' | 'On Break' | 'Logged Out';
}

// Chiamata live: DTO condiviso di `GET /voip/calls/active` + campi aggiuntivi
// che arrivano SOLO dagli eventi socket (monitoraggio, owner per il gating
// self/cross-user — Task #1336: `owner_user_id` impostato da
// unifiedCallEventService quando risolve extension → user).
interface ActiveCall extends VoipActiveCallListItem {
    tenant_id?: string;
    is_monitored?: boolean;
    monitored_by?: string;
    owner_user_id?: string | null;
}

type SupervisorAction = 'listen' | 'whisper' | 'barge' | 'hangup' | 'transfer' | 'hold' | 'unhold';
type ExtensionAction = 'call' | 'transfer_to' | 'toggle_dnd' | 'queue_login' | 'queue_logout';


interface RegisteredDevice {
    contact: string;
    user_agent: string;
    network_ip: string;
    registered_at: string;
    expires_in: number;
}

export default function SwitchboardPanel() {
    const { user } = useAuth();
    const { hasPermission } = usePermissions();
    const [extensions, setExtensions] = useState<ExtensionStatus[]>([]);
    const [activeCalls, setActiveCalls] = useState<ActiveCall[]>([]);
    const activeCallsRef = useRef<ActiveCall[]>([]);
    const [filter, setFilter] = useState('');
    const { isConnected, emit, on, off, joinRoom, leaveRoom, socketVersion } = useSocket();
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'calls' | 'extensions'>('calls');
    const [extensionFilter, setExtensionFilter] = useState<'all' | 'registered' | 'incall' | 'dnd' | 'offline'>('all');
    
    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        action: SupervisorAction;
        call: ActiveCall | null;
        title: string;
        description: string;
    }>({ open: false, action: 'listen', call: null, title: '', description: '' });
    
    const [transferDialog, setTransferDialog] = useState<{
        open: boolean;
        call: ActiveCall | null;
    }>({ open: false, call: null });
    const [transferDestination, setTransferDestination] = useState('');
    
    const [switchExtDialog, setSwitchExtDialog] = useState<{
        open: boolean;
        targetExtension: string;
        targetDisplayName: string;
    }>({ open: false, targetExtension: '', targetDisplayName: '' });
    
    const [dndActionPending, setDndActionPending] = useState<string | null>(null);
    
    const [actionFeedback, setActionFeedback] = useState<{
        show: boolean;
        type: 'success' | 'error' | 'warning';
        message: string;
    }>({ show: false, type: 'success', message: '' });

    const [sipCredentialsDialog, setSipCredentialsDialog] = useState<{
        open: boolean;
        extension: ExtensionStatus | null;
    }>({ open: false, extension: null });
    const [sipCredentials, setSipCredentials] = useState<SipCredentials | null>(null);
    const [sipDevices, setSipDevices] = useState<RegisteredDevice[]>([]);
    const [sipLoading, setSipLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [regeneratingPassword, setRegeneratingPassword] = useState(false);

    const [extensionGroups, setExtensionGroups] = useState<Array<{id: string, name: string, color: string}>>([]);
    const [selectedGroupFilter, setSelectedGroupFilter] = useState<string>('');
    const [groupMemberExtensions, setGroupMemberExtensions] = useState<string[]>([]);

    const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
    const userExtension = user?.extension;
    const sipPhone = useSipPhone();
    
    // Permesso dal contesto permessi (stessa fonte del resto della SPA): la
    // vecchia probe `/auth/check-permission/...` non era una rotta API e
    // restituiva sempre "nessun permesso".
    const hasSupervisorPermission = !!user && hasPermission('switchboard.supervisor');
    
    useEffect(() => {
        activeCallsRef.current = activeCalls;
    }, [activeCalls]);

    const canShowSupervisorActions = useCallback((call: ActiveCall): boolean => {
        if (!hasSupervisorPermission) return false;
        
        const status = call.status?.toLowerCase();
        if (status !== 'bridged') return false;
        
        const answeredBy = call.answered_by_extension;
        const calleeExt = call.extension || call.callee_number;
        const callerExt = call.caller_number;
        
        if (answeredBy === userExtension) return false;
        if (calleeExt === userExtension || callerExt === userExtension) return false;
        
        return true;
    }, [hasSupervisorPermission, userExtension]);

    useEffect(() => {
        if (user && userExtension) {
            sipPhone.ensureRegistered().catch(err => {
                console.warn('[Switchboard] SIP ensureRegistered failed:', err);
            });
        }
    }, [user, userExtension, sipPhone]);

    const loadExtensions = useCallback(async () => {
        try {
            const data = await apiClient.getExtensions({ limit: 100 });
            if (data?.success && data?.data) {
                // Task #3713 — getExtensions è tipizzato (data = { items, pagination }): niente cast.
                const items: SipExtensionApiItem[] = data.data?.items || [];
                console.log('[Switchboard] RAW API items sample:', items.slice(0, 3).map((i) => ({ ext: i.extension, dnd_enabled: i.dnd_enabled, type: typeof i.dnd_enabled })));
                const mapped: ExtensionStatus[] = items.map((item) => ({
                    id: item.id,
                    extension: item.extension,
                    name: item.display_name || item.extension,
                    status: (item.status === 'active' ? (item.is_registered ? 'available' : 'offline') : 'offline') as ExtensionStatus['status'],
                    department: 'General',
                    is_registered: item.is_registered,
                    dnd_enabled: item.dnd_enabled === true,
                    queue_logged_in: item.queue_logged_in === true
                }));
                console.log('[Switchboard] Mapped DND states:', mapped.map(e => ({ ext: e.extension, dnd: e.dnd_enabled })));
                setExtensions(mapped);
            }
        } catch (error) {
            console.error('Failed to load extensions', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadExtensions();
    }, [loadExtensions]);

    useEffect(() => {
        const loadGroups = async () => {
            try {
                const response = await apiClient.get('/extension-groups');
                if (response?.success && Array.isArray(response.data)) {
                    setExtensionGroups((response.data as ExtensionGroupApiItem[]).map((g) => ({ id: g.id, name: g.name, color: g.color || '#6b7280' })));
                }
            } catch (err) {
                console.warn('[Switchboard] Failed to load extension groups:', err);
            }
        };
        loadGroups();
    }, []);

    useEffect(() => {
        if (!selectedGroupFilter) {
            setGroupMemberExtensions([]);
            return;
        }
        const loadMembers = async () => {
            try {
                const response = await apiClient.get(`/extension-groups/${selectedGroupFilter}/members`);
                if (response?.success && Array.isArray(response.data)) {
                    setGroupMemberExtensions((response.data as ExtensionGroupMemberApiItem[]).map((m) => m.extension));
                }
            } catch (err) {
                console.warn('[Switchboard] Failed to load group members:', err);
                setGroupMemberExtensions([]);
            }
        };
        loadMembers();
    }, [selectedGroupFilter]);

    const loadActiveCalls = useCallback(async () => {
        if (!user) return;
        try {
            const data = await apiClient.get<VoipActiveCallListItem[]>('/voip/calls/active');
            if (data.success && Array.isArray(data.data)) {
                console.log('[Switchboard] Loaded active calls from API:', data.data);
                setActiveCalls(data.data);
            }
        } catch (error) {
            console.error('[Switchboard] Failed to load active calls:', error);
        }
    }, [user]);

    const initialLoadDone = useRef(false);
    const endedCallUuidsRef = useRef<Set<string>>(new Set());
    useEffect(() => {
        if (user && !initialLoadDone.current) {
            initialLoadDone.current = true;
            loadActiveCalls();
        }
    }, [user, loadActiveCalls]);

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveCalls(prev => prev.map(call => {
                const status = call.status?.toLowerCase();
                if (status === 'answered' || status === 'bridged') {
                    return { ...call, duration: (call.duration || 0) + 1 };
                }
                return call;
            }));
        }, 1000);
        
        return () => clearInterval(interval);
    }, []);

    const showFeedback = (type: 'success' | 'error' | 'warning', message: string) => {
        setActionFeedback({ show: true, type, message });
        setTimeout(() => setActionFeedback(prev => ({ ...prev, show: false })), type === 'warning' ? 8000 : 4000);
    };

    useEffect(() => {
        if (!user?.tenant_id || !isConnected) {
            console.log('[Switchboard] Waiting for socket connection...');
            return;
        }

        console.log('[Switchboard] Using shared socket (version:', socketVersion, ')');
        const callsRoom = `tenant:${user?.tenant_id}:calls`;
        const switchboardRoom = `tenant:${user?.tenant_id}:switchboard`;
        joinRoom(callsRoom);
        joinRoom(switchboardRoom);

        const handleCallsUpdate = (calls: ActiveCall[]) => {
            const validCalls = (calls || []).filter(c => !endedCallUuidsRef.current.has(c.uuid));
            setActiveCalls(validCalls);
            setLoading(false);
        };

        const handleCallCreated = (call: ActiveCall) => {
            console.log('[Switchboard] New call:', call);
            setActiveCalls(prev => [...prev.filter(c => c.uuid !== call.uuid), call]);
        };

        const handleCallUpdated = (call: ActiveCall) => {
            setActiveCalls(prev => prev.map(c => c.uuid === call.uuid ? call : c));
        };

        const handleCallAnswered = (call: ActiveCall) => {
            console.log('[Switchboard] Call answered:', call);
            setActiveCalls(prev => prev.map(c => c.uuid === call.uuid ? { ...c, ...call, status: call.status || 'answered' } : c));
        };

        const handleCallBridged = (call: ActiveCall) => {
            console.log('[Switchboard] Call bridged:', call);
            setActiveCalls(prev => prev.map(c => c.uuid === call.uuid ? { ...c, ...call, status: 'bridged', answer_time: call.answer_time || c.answer_time || new Date().toISOString() } : c));
        };

        const handleCallEnded = (data: { uuid: string }) => {
            console.log('[Switchboard] Call ended:', data.uuid);
            endedCallUuidsRef.current.add(data.uuid);
            setTimeout(() => {
                endedCallUuidsRef.current.delete(data.uuid);
            }, 30000);
            
            setActiveCalls(prev => prev.filter(c => c.uuid !== data.uuid));
            setConfirmDialog(prev => prev.call?.uuid === data.uuid 
                ? { open: false, action: 'listen', call: null, title: '', description: '' }
                : prev
            );
            setTransferDialog(prev => prev.call?.uuid === data.uuid
                ? { open: false, call: null }
                : prev
            );
        };

        const handleMonitoringStarted = (data: { callUuid: string; mode: string; supervisor: string }) => {
            setActiveCalls(prev => prev.map(c => 
                c.uuid === data.callUuid 
                    ? { ...c, is_monitored: true, monitored_by: data.supervisor }
                    : c
            ));
        };

        const handleActionResult = (result: SwitchboardActionResultEvent) => {
            if (result.success) {
                showFeedback('success', result.message || `${result.action} eseguito con successo`);
            } else {
                showFeedback('error', result.error || `Errore durante ${result.action}`);
            }
        };

        const handleExtensionStatusChange = (data: ExtensionStatusChangeEvent) => {
            setExtensions(prev => prev.map(ext => {
                const match = ext.extension === data.extension || (data.extension_id && ext.id === data.extension_id);
                if (!match) return ext;
                return {
                    ...ext,
                    is_registered: data.is_registered,
                };
            }));
        };

        const handleExtensionUpdate = (data: ExtensionUpdateEvent) => {
            console.log('[Switchboard] Received extension:update:', data);
            setExtensions(prev => prev.map(ext =>
                ext.extension === data.extension 
                    ? { 
                        ...ext, 
                        dnd_enabled: data.dnd_enabled !== undefined ? data.dnd_enabled : ext.dnd_enabled,
                        queue_logged_in: data.queue_logged_in !== undefined ? data.queue_logged_in : ext.queue_logged_in
                      } 
                    : ext
            ));
        };

        const handleAgentStatus = (data: QueueAgentStatusEvent) => {
            setExtensions(prev => prev.map(ext =>
                ext.extension === data.extensionNumber 
                    ? { ...ext, queue_agent_status: data.status } 
                    : ext
            ));
        };

        on('calls:update', handleCallsUpdate);
        on('call:created', handleCallCreated);
        on('call:updated', handleCallUpdated);
        on('call:answered', handleCallAnswered);
        on('call:bridged', handleCallBridged);
        on('call:ended', handleCallEnded);
        on('call:monitoring_started', handleMonitoringStarted);
        on('switchboard:actionResult', handleActionResult);
        on('sip:extension:status_change', handleExtensionStatusChange);
        on('extension:update', handleExtensionUpdate);
        on('queue:agent-status', handleAgentStatus);

        return () => {
            leaveRoom(callsRoom);
            leaveRoom(switchboardRoom);
            off('calls:update', handleCallsUpdate);
            off('call:created', handleCallCreated);
            off('call:updated', handleCallUpdated);
            off('call:answered', handleCallAnswered);
            off('call:bridged', handleCallBridged);
            off('call:ended', handleCallEnded);
            off('call:monitoring_started', handleMonitoringStarted);
            off('switchboard:actionResult', handleActionResult);
            off('sip:extension:status_change', handleExtensionStatusChange);
            off('extension:update', handleExtensionUpdate);
            off('queue:agent-status', handleAgentStatus);
        };
    }, [user?.tenant_id, isConnected, socketVersion, emit, on, off, joinRoom, leaveRoom]);

    const executeAction = (action: SupervisorAction, call: ActiveCall) => {
        if (!isConnected) return;

        // Task #1336 — gating delegato a helper puro (testato in isolamento).
        const currentUserId = user?.id;
        const { isSelfCall, requiresConfirm, targetExtension: targetExt } = decideSwitchboardGating(
            action,
            call,
            currentUserId,
            userExtension ?? undefined,
        );
        const crossUserSuffix = isSelfCall
            ? ''
            : ` ⚠️ AZIONE SU UTENTE TERZO (interno ${targetExt}). L'azione verrà registrata in audit log con il tuo nome utente.`;

        if (action === 'transfer') {
            if (!requiresConfirm) {
                // Self-transfer: salta l'AlertDialog di conferma e apri direttamente il dialog destinazione.
                setTransferDialog({ open: true, call });
                return;
            }
            // Cross-user: mostra conferma; il dialog destinazione si aprirà dopo "Conferma" (vedi confirmAction).
            setConfirmDialog({
                open: true,
                action: 'transfer',
                call,
                title: 'Trasferisci Chiamata',
                description: `Stai per trasferire la chiamata dell'interno ${targetExt}.${crossUserSuffix}`,
            });
            return;
        }

        // Self-bypass per Hangup/Barge: esegui senza conferma (la chiamata è propria,
        // l'utente ha pieno titolo per terminarla/entrarci direttamente).
        if (!requiresConfirm && (action === 'hangup' || action === 'barge')) {
            void executeActionConfirmed(action, call);
            return;
        }

        const actionLabels: Record<SupervisorAction, { title: string; description: string }> = {
            listen: {
                title: 'Ascolta Chiamata',
                description: `Ascolterai la chiamata corrente dell'interno al nuovo avvio SIP, che può essere diversa da quella visualizzata. Nessuna delle due parti ti sentirà direttamente; il badge "supervisione attiva" apparirà solo all'avvio effettivo.${crossUserSuffix}`
            },
            whisper: {
                title: 'Suggerisci all\'Operatore',
                description: `Parlerai all'operatore (${call.extension || call.callee_number}) nella chiamata corrente al nuovo avvio SIP, che può essere diversa da quella visualizzata. Il cliente NON ti sentirà.${crossUserSuffix}`
            },
            barge: {
                title: 'Entra nella Chiamata',
                description: `Entrerai nella chiamata corrente dell'interno al nuovo avvio SIP, che può essere diversa da quella visualizzata. ENTRAMBI (operatore e cliente) ti sentiranno.${crossUserSuffix}`
            },
            hangup: {
                title: 'Termina Chiamata',
                description: `Stai per terminare la chiamata tra ${call.caller_number} e ${call.callee_number}. Questa azione è irreversibile.${crossUserSuffix}`
            },
            hold: {
                title: 'Metti in Attesa',
                description: `Stai per mettere in attesa la chiamata.`
            },
            unhold: {
                title: 'Riprendi Chiamata',
                description: `Stai per riprendere la chiamata dall'attesa.`
            },
            transfer: { title: '', description: '' }
        };

        setConfirmDialog({
            open: true,
            action,
            call,
            ...actionLabels[action]
        });
    };

    // Task #1336 — esecuzione effettiva dell'azione, separata dal gating UI per
    // permettere il bypass-confirm (self-call) senza duplicare logica.
    async function executeActionConfirmed(action: SupervisorAction, call: ActiveCall): Promise<void> {
        if (!isConnected) return;

        if (action === 'transfer') {
            // Apri il dialog destinazione: la conferma cross-user è già passata.
            setTransferDialog({ open: true, call });
            return;
        }

        if (action === 'listen' || action === 'whisper' || action === 'barge') {
            let targetExtension = call.answered_by_extension;
            if (!targetExtension) targetExtension = call.extension;
            if (!targetExtension) {
                if (call.direction === 'inbound') {
                    targetExtension = /^\d{3,4}$/.test(call.callee_number) ? call.callee_number : undefined;
                } else if (call.direction === 'outbound') {
                    targetExtension = /^\d{3,4}$/.test(call.caller_number) ? call.caller_number : undefined;
                } else {
                    targetExtension = /^\d{3,4}$/.test(call.callee_number) ? call.callee_number :
                                      /^\d{3,4}$/.test(call.caller_number) ? call.caller_number : undefined;
                }
            }
            if (!targetExtension) {
                if (/^\d{3,4}$/.test(call.callee_number)) targetExtension = call.callee_number;
                else if (/^\d{3,4}$/.test(call.caller_number)) targetExtension = call.caller_number;
            }
            if (!targetExtension || !/^\d{3,4}$/.test(targetExtension)) {
                showFeedback('error', `Impossibile determinare l'interno da monitorare (caller: ${call.caller_number}, callee: ${call.callee_number}, answered_by: ${call.answered_by_extension || 'N/A'})`);
                return;
            }

            try {
                const success = await startSupervisorCall(
                    { mode: action, target_extension: targetExtension, target_call_uuid: call.uuid },
                    request => apiClient.post('/switchboard/supervisor/log', request),
                    number => sipPhone.call(number),
                );
                if (success) {
                    showFeedback('success', 'Richiesta SIP inviata; supervisione non ancora confermata. Il badge indica l’avvio effettivo.');
                } else {
                    showFeedback('error', 'Impossibile avviare la chiamata supervisor');
                }
            } catch (err) {
                // eslint-disable-next-line no-console -- surfacing exception to browser console for triage
                console.error('Supervisor call error:', err);
                showFeedback('error', err instanceof Error ? err.message : 'Errore nel preflight o nella connessione supervisor');
            }
            return;
        }

        // hangup / hold / unhold: passa per il socket switchboard:action (audit lato backend).
        emit('switchboard:action', {
            action,
            callUuid: call.uuid,
        });
    }

    const confirmAction = async () => {
        if (!isConnected || !confirmDialog.call) return;
        const action = confirmDialog.action;
        const call = confirmDialog.call;
        setConfirmDialog({ open: false, action: 'listen', call: null, title: '', description: '' });
        await executeActionConfirmed(action, call);
    };

    const executeTransfer = () => {
        if (!isConnected || !transferDialog.call || !transferDestination) return;

        emit('switchboard:action', {
            action: 'transfer',
            callUuid: transferDialog.call.uuid,
            destination: transferDestination
        });

        setTransferDialog({ open: false, call: null });
        setTransferDestination('');
    };

    const handleExtensionAction = async (ext: ExtensionStatus, action: ExtensionAction) => {
        if (!isAdmin && ext.extension !== userExtension) {
            showFeedback('error', 'Non hai i permessi per questa azione');
            return;
        }

        try {
            switch (action) {
                case 'call':
                    showFeedback('success', `Chiamata verso ${ext.extension}...`);
                    break;
                case 'toggle_dnd': {
                    if (dndActionPending === ext.id) return;
                    setDndActionPending(ext.id!);
                    try {
                        const newDndState = !ext.dnd_enabled;
                        setExtensions(prev => prev.map(e => 
                            e.id === ext.id ? { ...e, dnd_enabled: newDndState } : e
                        ));
                        const result = await apiClient.post(`/voip/sip-extensions/${ext.id}/dnd`, { enabled: newDndState } satisfies SipExtensionDndBody);
                        const feedback = getSipExtensionDndFeedback(result.data);
                        const savedEnabled = feedback.savedEnabled;
                        if (savedEnabled !== undefined) {
                            setExtensions(prev => prev.map(e =>
                                e.id === ext.id ? { ...e, dnd_enabled: savedEnabled } : e
                            ));
                        }
                        showFeedback(feedback.type, feedback.message);
                    } catch (dndErr) {
                        setExtensions(prev => prev.map(e => 
                            e.id === ext.id ? { ...e, dnd_enabled: ext.dnd_enabled } : e
                        ));
                        throw dndErr;
                    } finally {
                        setTimeout(() => setDndActionPending(null), 500);
                    }
                    break;
                }
                case 'queue_login':
                case 'queue_logout': {
                    const result = await apiClient.post<SipExtensionQueueResponse>(
                        `/voip/sip-extensions/${ext.id}/${action === 'queue_login' ? 'queue-login' : 'queue-logout'}`,
                    );
                    const feedback = getSipExtensionQueueFeedback(result.data);
                    const savedLoggedIn = feedback.savedLoggedIn;
                    if (savedLoggedIn !== undefined) {
                        setExtensions(prev => prev.map(e =>
                            e.id === ext.id ? { ...e, queue_logged_in: savedLoggedIn } : e
                        ));
                    }
                    showFeedback(feedback.type, feedback.message);
                    break;
                }
            }
        } catch (error) {
            showFeedback('error', getErrorMessage(error) || 'Errore durante l\'azione');
        }
    };

    const resetAgentFromBreak = async (extensionNumber: string) => {
        try {
            const response = await apiClient.post(`/voip/queues/agents/${extensionNumber}/reset-break`);
            if (response?.success) {
                showFeedback('success', `Agente ${extensionNumber} ripristinato`);
            } else {
                showFeedback('error', response?.message || 'Errore nel ripristino');
            }
        } catch (error) {
            showFeedback('error', getErrorMessage(error) || 'Errore nel ripristino agente');
        }
    };

    const openSipCredentialsDialog = async (ext: ExtensionStatus) => {
        if (!isAdmin && ext.extension !== userExtension) {
            showFeedback('error', 'Non hai i permessi per visualizzare le credenziali');
            return;
        }
        
        setSipCredentialsDialog({ open: true, extension: ext });
        setSipLoading(true);
        setShowPassword(false);
        setSipCredentials(null);
        setSipDevices([]);

        try {
            const [credResponse, devicesResponse] = await Promise.all([
                apiClient.get<SipCredentials>(`/voip/sip-extensions/${ext.id}/sip-credentials`),
                apiClient.get<{ devices?: RegisteredDevice[] }>(`/voip/sip-extensions/${ext.id}/devices`)
            ]);

            if (credResponse.success && credResponse.data) {
                setSipCredentials(sipExtensionProvisioningSchema.parse(credResponse.data));
            }
            if (devicesResponse.success && devicesResponse.data) {
                setSipDevices((devicesResponse.data).devices || []);
            }
        } catch (error) {
            showFeedback('error', getErrorMessage(error) || 'Errore nel caricamento credenziali');
        } finally {
            setSipLoading(false);
        }
    };

    const closeSipCredentialsDialog = () => {
        setSipCredentialsDialog({ open: false, extension: null });
        setSipCredentials(null);
        setSipDevices([]);
        setShowPassword(false);
    };

    const regenerateSipPassword = async () => {
        if (!sipCredentialsDialog.extension) return;
        
        setRegeneratingPassword(true);
        try {
            const response = await apiClient.put<SipExtensionRotationDto>(`/voip/sip-extensions/${sipCredentialsDialog.extension.id}/sip-credentials`, {
                regenerate: true
            });
            if (response.success && response.data) {
                // Cattura fuori dal closure: il narrowing di response.data non sopravvive dentro il setter.
                const regenerated = sipExtensionRotationSchema.parse(response.data);
                setSipCredentials(prev => prev ? { ...prev, sip_password: regenerated.sip_password, password: regenerated.sip_password } : null);
                showFeedback('success', 'Password SIP rigenerata con successo');
            }
        } catch (error) {
            showFeedback('error', getErrorMessage(error) || 'Errore nella rigenerazione password');
        } finally {
            setRegeneratingPassword(false);
        }
    };

    const copyToClipboard = async (text: string, label: string) => {
        try {
            await navigator.clipboard.writeText(text);
            showFeedback('success', `${label} copiato negli appunti`);
        } catch (error) {
            showFeedback('error', 'Errore nella copia');
        }
    };

    const formatDuration = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'available': return 'bg-green-500';
            case 'busy': case 'answered': case 'bridged': return 'bg-red-500';
            case 'ringing': return 'bg-yellow-500 animate-pulse';
            case 'on_hold': return 'bg-blue-500';
            case 'dnd': return 'bg-orange-500';
            case 'away': return 'bg-orange-400';
            default: return 'bg-gray-500';
        }
    };
    
    const getCallStatusLabel = (status: string, queueName?: string) => {
        switch (status) {
            case 'ringing':
                return 'Squilla';
            case 'queued':
                return 'In Coda';
            case 'answered':
                return 'In Corso';
            case 'bridged':
                return 'In Corso';
            case 'on_hold':
                return 'In Attesa';
            case 'muted':
                return 'Silenziata';
            default:
                return status;
        }
    };
    
    const getCallStatusColor = (status: string, queueName?: string) => {
        switch (status) {
            case 'ringing':
                return 'bg-yellow-500 animate-pulse';
            case 'queued':
                return 'bg-orange-500 animate-pulse';
            case 'answered':
                return 'bg-green-500';
            case 'bridged':
                return 'bg-green-500';
            case 'on_hold':
                return 'bg-blue-500';
            case 'muted':
                return 'bg-purple-500';
            default:
                return 'bg-gray-500';
        }
    };

    const getStatusBorderColor = (ext: ExtensionStatus & { activeCall?: ActiveCall }) => {
        if (ext.status === 'ringing') {
            return 'border-l-yellow-500';
        }
        if (ext.status === 'busy' || ext.activeCall) {
            return 'border-l-red-500';
        }
        if (ext.dnd_enabled || ext.status === 'dnd') {
            return 'border-l-orange-500';
        }
        if (ext.is_registered && ext.status === 'available') {
            return 'border-l-green-500';
        }
        return 'border-l-gray-500';
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'available': return 'Disponibile';
            case 'busy': case 'answered': case 'bridged': return 'In chiamata';
            case 'ringing': return 'Squilla';
            case 'on_hold': return 'In attesa';
            case 'dnd': return 'Non disturbare';
            case 'away': return 'Assente';
            default: return 'Offline';
        }
    };

    const extensionsWithCalls = useMemo(() => {
        return extensions.map(ext => {
            const activeCall = activeCalls.find(call => 
                call.extension === ext.extension ||
                call.callee_number === ext.extension ||
                call.caller_number === ext.extension
            );
            
            const isUserExtension = ext.extension === userExtension;
            const isRegistered = isUserExtension 
                ? (sipPhone.isRegistered || sipPhone.fsIsRegistered) 
                : ext.is_registered;
            
            let status: ExtensionStatus['status'] = ext.dnd_enabled ? 'dnd' : 
                (isRegistered ? 'available' : 'offline');
            if (activeCall) {
                status = activeCall.status === 'ringing' ? 'ringing' : 'busy';
            }
            
            return { ...ext, activeCall, status, is_registered: isRegistered };
        });
    }, [extensions, activeCalls, userExtension, sipPhone.isRegistered, sipPhone.fsIsRegistered]);

    const segmentedExtensions = useMemo(() => {
        const registered = extensionsWithCalls.filter(e => e.is_registered && e.status === 'available');
        const incall = extensionsWithCalls.filter(e => e.status === 'busy' || e.status === 'ringing');
        const dnd = extensionsWithCalls.filter(e => e.dnd_enabled || e.status === 'dnd');
        const offline = extensionsWithCalls.filter(e => !e.is_registered && !e.dnd_enabled);
        
        return { registered, incall, dnd, offline };
    }, [extensionsWithCalls]);

    const filteredExtensions = useMemo(() => {
        let list = extensionsWithCalls;
        
        if (extensionFilter !== 'all') {
            list = segmentedExtensions[extensionFilter] || [];
        }
        
        if (selectedGroupFilter && groupMemberExtensions.length > 0) {
            list = list.filter(ext => groupMemberExtensions.includes(ext.extension));
        }
        
        if (filter) {
            list = list.filter(ext =>
                ext.name.toLowerCase().includes(filter.toLowerCase()) ||
                ext.extension.includes(filter)
            );
        }
        
        return list;
    }, [extensionsWithCalls, extensionFilter, filter, segmentedExtensions, selectedGroupFilter, groupMemberExtensions]);

    const filteredCalls = useMemo(() => {
        return activeCalls.filter(call =>
            (call.caller_number?.includes(filter)) ||
            (call.callee_number?.includes(filter)) ||
            (call.caller_name?.toLowerCase().includes(filter.toLowerCase())) ||
            (call.extension?.includes(filter))
        );
    }, [activeCalls, filter]);

    const openDialpad = () => {
        sipPhone.openPhone();
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <TooltipProvider>
        <div className="h-[calc(100vh-4rem)] flex flex-col gap-3 md:gap-4 p-3 md:p-6 animate-fade-in-up bg-background text-foreground overflow-hidden">
            {actionFeedback.show && (
                <div role="status" aria-live="polite" className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 max-w-[calc(100vw-2rem)] sm:max-w-lg ${
                    actionFeedback.type === 'success' 
                        ? 'bg-green-500/90 text-white' 
                        : actionFeedback.type === 'warning'
                            ? 'bg-amber-100 text-amber-950 border border-amber-500'
                            : 'bg-red-500/90 text-white'
                }`}>
                    {actionFeedback.type === 'success' ? (
                        <Check className="h-5 w-5" />
                    ) : (
                        <AlertCircle className="h-5 w-5" />
                    )}
                    <span>{actionFeedback.message}</span>
                </div>
            )}

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <AppPageHeader
                    noBorder
                    className="pb-0 flex-1"
                    title="Pannello Operatore"
                    subtitle={isAdmin ? 'Supervisione chiamate e gestione estensioni' : 'Monitoraggio chiamate'}
                />

                <div className="flex items-center gap-3 w-full md:w-auto bg-muted/20 p-2 rounded-xl border border-border backdrop-blur-md">
                    <div className="relative flex-1 md:flex-none">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                            placeholder="Cerca..."
                            className="pl-9 w-full md:w-64 bg-transparent border-none text-foreground placeholder:text-muted-foreground h-9"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                        />
                    </div>
                    <div className="h-6 w-px bg-border" />
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setViewMode('grid')}
                        className={`h-8 w-8 ${viewMode === 'grid' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`}
                    >
                        <LayoutGrid className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setViewMode('list')}
                        className={`h-8 w-8 ${viewMode === 'list' ? 'bg-background shadow-sm text-foreground' : 'text-muted-foreground'}`}
                    >
                        <ListIcon className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={loadExtensions}
                        className="h-8 w-8 text-muted-foreground hover:text-foreground"
                    >
                        <RefreshCw className="h-4 w-4" />
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <Card className="glass-card p-3">
                    <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-green-500" />
                        <span className="text-muted-foreground">Chiamate attive</span>
                    </div>
                    <div className="text-2xl font-bold mt-1">{activeCalls.length}</div>
                </Card>
                <Card className="glass-card p-3">
                    <div className="flex items-center gap-2">
                        <UserCheck className="h-4 w-4 text-green-500" />
                        <span className="text-muted-foreground">Registrati</span>
                    </div>
                    <div className="text-2xl font-bold mt-1">{segmentedExtensions.registered.length}</div>
                </Card>
                <Card className="glass-card p-3">
                    <div className="flex items-center gap-2">
                        <PhoneIncoming className="h-4 w-4 text-blue-500" />
                        <span className="text-muted-foreground">In chiamata</span>
                    </div>
                    <div className="text-2xl font-bold mt-1">{segmentedExtensions.incall.length}</div>
                </Card>
                <Card className="glass-card p-3">
                    <div className="flex items-center gap-2">
                        <UserX className="h-4 w-4 text-gray-500" />
                        <span className="text-muted-foreground">Offline</span>
                    </div>
                    <div className="text-2xl font-bold mt-1">{segmentedExtensions.offline.length}</div>
                </Card>
            </div>

            <div className="flex-1 flex flex-col min-h-0">
                <div className="flex gap-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg w-fit border border-gray-200 dark:border-gray-700">
                    <button
                        onClick={() => setActiveTab('calls')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            activeTab === 'calls' 
                                ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm' 
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                        }`}
                    >
                        <Phone className="h-4 w-4" />
                        Chiamate Attive ({activeCalls.length})
                    </button>
                    <button
                        onClick={() => setActiveTab('extensions')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            activeTab === 'extensions' 
                                ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-white shadow-sm' 
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                        }`}
                    >
                        <Users className="h-4 w-4" />
                        Estensioni ({extensions.length})
                    </button>
                </div>

                {activeTab === 'calls' && (
                <div className="flex-1 mt-4 min-h-0">
                    <ScrollArea className="h-full">
                        {activeCalls.length === 0 ? (
                            <Card className="glass-card">
                                <CardContent className="py-12 text-center">
                                    <Phone className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                                    <p className="text-muted-foreground">Nessuna chiamata attiva</p>
                                    <p className="text-sm text-muted-foreground/60 mt-2">
                                        Le chiamate in corso appariranno qui in tempo reale
                                    </p>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="space-y-3 pb-4">
                                {filteredCalls.map((call) => (
                                    <Card key={call.uuid} className={`glass-card overflow-hidden transition-all ${
                                        call.status === 'ringing' ? 'border-yellow-500/50 animate-pulse' : 
                                        call.is_monitored ? 'border-purple-500/50' : ''
                                    }`}>
                                        <CardContent className="p-4">
                                            <div className="flex items-center justify-between gap-4">
                                                <div className="flex items-center gap-4 flex-1 min-w-0">
                                                    <div className={`p-3 rounded-full ${
                                                        call.direction === 'inbound' 
                                                            ? 'bg-green-500/10 text-green-500' 
                                                            : call.direction === 'outbound'
                                                            ? 'bg-blue-500/10 text-blue-500'
                                                            : 'bg-purple-500/10 text-purple-500'
                                                    }`}>
                                                        {call.direction === 'inbound' ? (
                                                            <ArrowDownLeft className="h-5 w-5" />
                                                        ) : call.direction === 'outbound' ? (
                                                            <ArrowUpRight className="h-5 w-5" />
                                                        ) : (
                                                            <Phone className="h-5 w-5" />
                                                        )}
                                                    </div>

                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                            <span className="font-mono font-bold text-sm md:text-lg">
                                                                {call.caller_number}
                                                            </span>
                                                            <span className="text-muted-foreground">→</span>
                                                            <span className="font-mono font-bold text-sm md:text-lg">
                                                                {call.callee_number}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                                                            {call.caller_name && (
                                                                <span>{call.caller_name}</span>
                                                            )}
                                                            {call.extension && (
                                                                <Badge variant="outline" className="font-mono text-xs">
                                                                    Ext. {call.extension}
                                                                </Badge>
                                                            )}
                                                            {call.queue_name && (
                                                                <Badge variant="secondary" className="text-xs">
                                                                    {call.queue_name}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="flex flex-col items-end gap-1">
                                                        <Badge className={`${getCallStatusColor(call.status, call.queue_name ?? undefined)} text-white uppercase text-xs`}>
                                                            {getCallStatusLabel(call.status, call.queue_name ?? undefined)}
                                                        </Badge>
                                                        <div className="flex items-center gap-1 text-sm text-muted-foreground font-mono">
                                                            <Clock className="h-3 w-3" />
                                                            {formatDuration(call.duration)}
                                                        </div>
                                                        {call.is_monitored && (
                                                            <Badge variant="outline" className="text-purple-500 border-purple-500/50 text-xs">
                                                                <Eye className="h-3 w-3 mr-1" />
                                                                Monitorata
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </div>

                                                {canShowSupervisorActions(call) && (
                                                    <div className="flex items-center gap-2">
                                                        <Separator orientation="vertical" className="h-12" />
                                                        
                                                        <div className="flex flex-wrap gap-1">
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8 text-green-500 hover:text-green-600 hover:bg-green-500/10"
                                                                        onClick={() => executeAction('listen', call)}
                                                                    >
                                                                        <Headphones className="h-4 w-4" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Ascolta (silenzioso)</TooltipContent>
                                                            </Tooltip>

                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8 text-blue-500 hover:text-blue-600 hover:bg-blue-500/10"
                                                                        onClick={() => executeAction('whisper', call)}
                                                                    >
                                                                        <Mic className="h-4 w-4" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Suggerisci (solo operatore)</TooltipContent>
                                                            </Tooltip>

                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8 text-purple-500 hover:text-purple-600 hover:bg-purple-500/10"
                                                                        onClick={() => executeAction('barge', call)}
                                                                    >
                                                                        <Users className="h-4 w-4" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Entra nella chiamata</TooltipContent>
                                                            </Tooltip>

                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8 text-orange-500 hover:text-orange-600 hover:bg-orange-500/10"
                                                                        onClick={() => executeAction(call.status === 'on_hold' ? 'unhold' : 'hold', call)}
                                                                    >
                                                                        {call.status === 'on_hold' ? (
                                                                            <Play className="h-4 w-4" />
                                                                        ) : (
                                                                            <Pause className="h-4 w-4" />
                                                                        )}
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    {call.status === 'on_hold' ? 'Riprendi' : 'Metti in attesa'}
                                                                </TooltipContent>
                                                            </Tooltip>

                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8 text-cyan-500 hover:text-cyan-600 hover:bg-cyan-500/10"
                                                                        onClick={() => executeAction('transfer', call)}
                                                                    >
                                                                        <PhoneForwarded className="h-4 w-4" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Trasferisci</TooltipContent>
                                                            </Tooltip>

                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-500/10"
                                                                        onClick={() => executeAction('hangup', call)}
                                                                    >
                                                                        <PhoneOff className="h-4 w-4" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Termina chiamata</TooltipContent>
                                                            </Tooltip>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </ScrollArea>
                </div>
                )}

                {activeTab === 'extensions' && (
                <div className="flex-1 mt-4 min-h-0 flex flex-col gap-4">
                    <div className="flex gap-1.5 md:gap-2 flex-wrap">
                        {[
                            { value: 'all', label: 'Tutti', count: extensionsWithCalls.length },
                            { value: 'registered', label: 'Registrati', count: segmentedExtensions.registered.length, color: 'text-green-500' },
                            { value: 'incall', label: 'In chiamata', count: segmentedExtensions.incall.length, color: 'text-blue-500' },
                            { value: 'dnd', label: 'DND', count: segmentedExtensions.dnd.length, color: 'text-red-500' },
                            { value: 'offline', label: 'Offline', count: segmentedExtensions.offline.length, color: 'text-gray-500' },
                        ].map(item => (
                            <Button
                                key={item.value}
                                variant={extensionFilter === item.value ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setExtensionFilter(item.value as 'all' | 'registered' | 'incall' | 'dnd' | 'offline')}
                                className={`${extensionFilter !== item.value && item.color ? item.color : ''}`}
                            >
                                {item.label}
                                <Badge variant="secondary" className="ml-2 text-xs">
                                    {item.count}
                                </Badge>
                            </Button>
                        ))}
                        {extensionGroups.length > 0 && (
                            <Select value={selectedGroupFilter} onValueChange={(val) => setSelectedGroupFilter(val === 'all' ? '' : val)}>
                                <SelectTrigger className="w-[200px] h-9">
                                    <SelectValue placeholder="Filtra per Gruppo" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tutti i Gruppi</SelectItem>
                                    {extensionGroups.map((group) => (
                                        <SelectItem key={group.id} value={group.id}>
                                            <div className="flex items-center gap-2">
                                                <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: group.color }} />
                                                {group.name}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                    </div>

                    <ScrollArea className="flex-1">
                        {viewMode === 'grid' ? (
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-3 pb-4">
                                {filteredExtensions.map((ext) => {
                                    const isMyExtension = ext.extension === userExtension;
                                    const canOperate = isMyExtension || isAdmin;
                                    
                                    return (
                                    <Card 
                                        key={ext.extension} 
                                        className={`glass-card relative overflow-hidden transition-all hover:shadow-lg border-l-4 ${getStatusBorderColor(ext)} ${ext.status === 'ringing' ? 'animate-pulse' : ''}`}
                                        draggable={!!ext.activeCall}
                                        onDragStart={(e) => {
                                            if (ext.activeCall) {
                                                e.dataTransfer.setData('callUuid', ext.activeCall.uuid);
                                                e.dataTransfer.setData('fromExtension', ext.extension);
                                            }
                                        }}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            const callUuid = e.dataTransfer.getData('callUuid');
                                            const fromExt = e.dataTransfer.getData('fromExtension');
                                            if (callUuid && fromExt !== ext.extension && isConnected) {
                                                emit('switchboard:action', {
                                                    action: 'transfer',
                                                    callUuid,
                                                    destination: ext.extension
                                                });
                                                showFeedback('success', `Trasferimento verso ${ext.extension}...`);
                                            }
                                        }}
                                    >
                                        <div className="p-4">
                                            <div className="flex items-start justify-between mb-2">
                                                <div className="flex items-center gap-2">
                                                    <div className={`h-3 w-3 rounded-full ${getStatusColor(ext.status)} shadow-lg`} />
                                                    <span className="font-mono font-bold text-lg">{ext.extension}</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    {ext.dnd_enabled && (
                                                        <Moon className="h-4 w-4 text-orange-500" />
                                                    )}
                                                    {ext.queue_logged_in && (
                                                        <Users className="h-4 w-4 text-blue-500" />
                                                    )}
                                                    {(isAdmin || ext.extension === userExtension) && (
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <button 
                                                                    className="h-5 w-5 flex items-center justify-center rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        openSipCredentialsDialog(ext);
                                                                    }}
                                                                >
                                                                    <Settings className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                                                                </button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>Credenziali SIP</TooltipContent>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            <p className="text-sm font-medium truncate">{ext.name}</p>
                                            <div className="flex items-center gap-1 mb-2">
                                                <p className="text-xs text-muted-foreground">{getStatusLabel(ext.status)}</p>
                                                {ext.queue_agent_status === 'On Break' && (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Badge 
                                                                variant="destructive" 
                                                                className="text-[10px] px-1 py-0 h-4 cursor-pointer hover:bg-red-600"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    resetAgentFromBreak(ext.extension);
                                                                }}
                                                            >
                                                                On Break ✕
                                                            </Badge>
                                                        </TooltipTrigger>
                                                        <TooltipContent>Clicca per ripristinare agente</TooltipContent>
                                                    </Tooltip>
                                                )}
                                            </div>

                                            {ext.activeCall && (
                                                <div className="text-xs bg-black/20 dark:bg-white/10 rounded-lg p-2 mb-3 border border-current/20">
                                                    <div className="flex items-center gap-2">
                                                        {ext.activeCall.direction === 'inbound' ? (
                                                            <ArrowDownLeft className="h-3 w-3 text-green-400 flex-shrink-0" />
                                                        ) : (
                                                            <ArrowUpRight className="h-3 w-3 text-blue-400 flex-shrink-0" />
                                                        )}
                                                        <span className="font-mono truncate font-medium">
                                                            {ext.activeCall.direction === 'inbound' ? ext.activeCall.caller_number : ext.activeCall.callee_number}
                                                        </span>
                                                    </div>
                                                    {ext.activeCall.caller_name && (
                                                        <p className="truncate text-muted-foreground mt-1 ml-5">{ext.activeCall.caller_name}</p>
                                                    )}
                                                    <div className="flex items-center gap-1 mt-1 ml-5 font-mono text-muted-foreground">
                                                        <Clock className="h-3 w-3" />
                                                        {formatDuration(ext.activeCall.duration)}
                                                    </div>
                                                </div>
                                            )}

                                            {ext.activeCall && canShowSupervisorActions(ext.activeCall) && (
                                                <div className="flex gap-1 flex-wrap mb-2 p-1.5 bg-black/5 dark:bg-white/5 rounded-lg">
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-6 w-6 text-green-500 hover:bg-green-500/10"
                                                                onClick={(e) => { e.stopPropagation(); executeAction('listen', ext.activeCall!); }}
                                                            >
                                                                <Headphones className="h-3 w-3" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>Ascolta</TooltipContent>
                                                    </Tooltip>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-6 w-6 text-blue-500 hover:bg-blue-500/10"
                                                                onClick={(e) => { e.stopPropagation(); executeAction('whisper', ext.activeCall!); }}
                                                            >
                                                                <Mic className="h-3 w-3" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>Suggerisci</TooltipContent>
                                                    </Tooltip>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-6 w-6 text-purple-500 hover:bg-purple-500/10"
                                                                onClick={(e) => { e.stopPropagation(); executeAction('barge', ext.activeCall!); }}
                                                            >
                                                                <Users className="h-3 w-3" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>Entra</TooltipContent>
                                                    </Tooltip>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-6 w-6 text-orange-500 hover:bg-orange-500/10"
                                                                onClick={(e) => { e.stopPropagation(); executeAction(ext.activeCall!.status! === 'on_hold' ? 'unhold' : 'hold', ext.activeCall!); }}
                                                            >
                                                                {ext.activeCall.status === 'on_hold' ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>{ext.activeCall.status === 'on_hold' ? 'Riprendi' : 'Attesa'}</TooltipContent>
                                                    </Tooltip>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-6 w-6 text-cyan-500 hover:bg-cyan-500/10"
                                                                onClick={(e) => { e.stopPropagation(); executeAction('transfer', ext.activeCall!); }}
                                                            >
                                                                <PhoneForwarded className="h-3 w-3" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>Trasferisci</TooltipContent>
                                                    </Tooltip>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-6 w-6 text-red-500 hover:bg-red-500/10"
                                                                onClick={(e) => { e.stopPropagation(); executeAction('hangup', ext.activeCall!); }}
                                                            >
                                                                <PhoneOff className="h-3 w-3" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>Termina</TooltipContent>
                                                    </Tooltip>
                                                </div>
                                            )}

                                            <div className="flex gap-1 flex-wrap justify-between">
                                                <div className="flex gap-1">
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-7 w-7 hover:bg-black/10 dark:hover:bg-white/10"
                                                                onClick={(e) => { 
                                                                    e.stopPropagation(); 
                                                                    if (isMyExtension && sipPhone.activeExtension && sipPhone.activeExtension !== userExtension) {
                                                                        setSwitchExtDialog({
                                                                            open: true,
                                                                            targetExtension: ext.extension,
                                                                            targetDisplayName: ext.name || ext.extension
                                                                        });
                                                                    } else if (isMyExtension) {
                                                                        sipPhone.openPhone();
                                                                    } else if (isAdmin) {
                                                                        setSwitchExtDialog({
                                                                            open: true,
                                                                            targetExtension: ext.extension,
                                                                            targetDisplayName: ext.name || ext.extension
                                                                        });
                                                                    }
                                                                }}
                                                            >
                                                                <Hash className="h-3.5 w-3.5" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>{isMyExtension && sipPhone.activeExtension && sipPhone.activeExtension !== userExtension ? `Torna a ${ext.extension}` : isMyExtension ? 'Apri tastierino' : `Passa a ${ext.extension}`}</TooltipContent>
                                                    </Tooltip>

                                                    {(isAdmin || ext.extension === userExtension) && (
                                                        <>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        className={`h-7 w-7 hover:bg-black/10 dark:hover:bg-white/10 ${ext.dnd_enabled ? 'text-orange-500' : ''}`}
                                                                        onClick={(e) => { e.stopPropagation(); handleExtensionAction(ext, 'toggle_dnd'); }}
                                                                    >
                                                                        <Moon className="h-3 w-3" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            {ext.dnd_enabled ? 'Disattiva DND' : 'Attiva DND'}
                                                        </TooltipContent>
                                                    </Tooltip>

                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className={`h-7 w-7 ${ext.queue_logged_in ? 'text-green-500' : 'text-muted-foreground'}`}
                                                                onClick={(e) => { e.stopPropagation(); handleExtensionAction(ext, ext.queue_logged_in ? 'queue_logout' : 'queue_login'); }}
                                                            >
                                                                {ext.queue_logged_in ? (
                                                                    <LogOut className="h-3 w-3" />
                                                                ) : (
                                                                    <LogIn className="h-3 w-3" />
                                                                )}
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            {ext.queue_logged_in ? 'Logout Coda' : 'Login Coda'}
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </>
                                            )}

                                                </div>

                                                {activeCalls.length > 0 && (
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-7 w-7 text-cyan-500 hover:bg-black/10 dark:hover:bg-white/10"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setTransferDestination(ext.extension);
                                                                    if (activeCalls[0]) {
                                                                        setTransferDialog({ open: true, call: activeCalls[0] });
                                                                    }
                                                                }}
                                                            >
                                                                <PhoneForwarded className="h-3.5 w-3.5" />
                                                            </Button>
                                                        </TooltipTrigger>
                                                        <TooltipContent>Trasferisci qui</TooltipContent>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        </div>
                                    </Card>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="space-y-2 pb-4">
                                {filteredExtensions.map((ext) => {
                                    const isMyExtension = ext.extension === userExtension;
                                    const canOperate = isMyExtension || isAdmin;
                                    
                                    return (
                                    <Card 
                                        key={ext.extension} 
                                        className={`glass-card border-l-4 transition-all hover:shadow-lg ${getStatusBorderColor(ext)} ${ext.status === 'ringing' ? 'animate-pulse' : ''}`}
                                        draggable={!!ext.activeCall}
                                        onDragStart={(e) => {
                                            if (ext.activeCall) {
                                                e.dataTransfer.setData('callUuid', ext.activeCall.uuid);
                                                e.dataTransfer.setData('fromExtension', ext.extension);
                                            }
                                        }}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            const callUuid = e.dataTransfer.getData('callUuid');
                                            const fromExt = e.dataTransfer.getData('fromExtension');
                                            if (callUuid && fromExt !== ext.extension && isConnected) {
                                                emit('switchboard:action', {
                                                    action: 'transfer',
                                                    callUuid,
                                                    destination: ext.extension
                                                });
                                                showFeedback('success', `Trasferimento verso ${ext.extension}...`);
                                            }
                                        }}
                                    >
                                        <CardContent className="p-3">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center gap-4">
                                                    <div className={`h-3 w-3 rounded-full ${getStatusColor(ext.status)} shadow-lg`} />
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-mono font-bold">{ext.extension}</span>
                                                            <span className="text-sm">{ext.name}</span>
                                                            {ext.dnd_enabled && <Moon className="h-3 w-3 text-orange-500" />}
                                                            {ext.queue_logged_in && <Users className="h-3 w-3 text-blue-500" />}
                                                            {ext.queue_agent_status === 'On Break' && (
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <Badge 
                                                                            variant="destructive" 
                                                                            className="text-[10px] px-1 py-0 h-4 cursor-pointer hover:bg-red-600"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                resetAgentFromBreak(ext.extension);
                                                                            }}
                                                                        >
                                                                            On Break ✕
                                                                        </Badge>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>Clicca per ripristinare</TooltipContent>
                                                                </Tooltip>
                                                            )}
                                                        </div>
                                                        <p className="text-xs text-muted-foreground">{getStatusLabel(ext.status)}</p>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    {ext.activeCall && (
                                                        <Badge variant="secondary" className="text-xs">
                                                            <Phone className="h-3 w-3 mr-1" />
                                                            {ext.activeCall.direction === 'inbound' ? ext.activeCall.caller_number : ext.activeCall.callee_number}
                                                            <span className="ml-1 font-mono">{formatDuration(ext.activeCall.duration)}</span>
                                                        </Badge>
                                                    )}

                                                    {ext.activeCall && canShowSupervisorActions(ext.activeCall) && (
                                                        <div className="flex gap-0.5 p-1 bg-black/5 dark:bg-white/5 rounded">
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button variant="ghost" size="icon" className="h-6 w-6 text-green-500 hover:bg-green-500/10" onClick={(e) => { e.stopPropagation(); executeAction('listen', ext.activeCall!); }}>
                                                                        <Headphones className="h-3 w-3" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Ascolta</TooltipContent>
                                                            </Tooltip>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button variant="ghost" size="icon" className="h-6 w-6 text-blue-500 hover:bg-blue-500/10" onClick={(e) => { e.stopPropagation(); executeAction('whisper', ext.activeCall!); }}>
                                                                        <Mic className="h-3 w-3" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Suggerisci</TooltipContent>
                                                            </Tooltip>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button variant="ghost" size="icon" className="h-6 w-6 text-purple-500 hover:bg-purple-500/10" onClick={(e) => { e.stopPropagation(); executeAction('barge', ext.activeCall!); }}>
                                                                        <Users className="h-3 w-3" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Entra</TooltipContent>
                                                            </Tooltip>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button variant="ghost" size="icon" className="h-6 w-6 text-orange-500 hover:bg-orange-500/10" onClick={(e) => { e.stopPropagation(); executeAction(ext.activeCall!.status! === 'on_hold' ? 'unhold' : 'hold', ext.activeCall!); }}>
                                                                        {ext.activeCall.status === 'on_hold' ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>{ext.activeCall.status === 'on_hold' ? 'Riprendi' : 'Attesa'}</TooltipContent>
                                                            </Tooltip>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button variant="ghost" size="icon" className="h-6 w-6 text-cyan-500 hover:bg-cyan-500/10" onClick={(e) => { e.stopPropagation(); executeAction('transfer', ext.activeCall!); }}>
                                                                        <PhoneForwarded className="h-3 w-3" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Trasferisci</TooltipContent>
                                                            </Tooltip>
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500 hover:bg-red-500/10" onClick={(e) => { e.stopPropagation(); executeAction('hangup', ext.activeCall!); }}>
                                                                        <PhoneOff className="h-3 w-3" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Termina</TooltipContent>
                                                            </Tooltip>
                                                        </div>
                                                    )}
                                                    
                                                    <div className="flex gap-1">
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button 
                                                                    variant="ghost" 
                                                                    size="icon" 
                                                                    className="h-7 w-7"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        if (isMyExtension && sipPhone.activeExtension && sipPhone.activeExtension !== userExtension) {
                                                                            setSwitchExtDialog({
                                                                                open: true,
                                                                                targetExtension: ext.extension,
                                                                                targetDisplayName: ext.name || ext.extension
                                                                            });
                                                                        } else if (isMyExtension) {
                                                                            sipPhone.openPhone();
                                                                        } else if (isAdmin) {
                                                                            setSwitchExtDialog({
                                                                                open: true,
                                                                                targetExtension: ext.extension,
                                                                                targetDisplayName: ext.name || ext.extension
                                                                            });
                                                                        }
                                                                    }}
                                                                >
                                                                    <Hash className="h-3 w-3" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>{isMyExtension && sipPhone.activeExtension && sipPhone.activeExtension !== userExtension ? `Torna a ${ext.extension}` : isMyExtension ? 'Apri tastierino' : `Passa a ${ext.extension}`}</TooltipContent>
                                                        </Tooltip>
                                                        {(isAdmin || ext.extension === userExtension) && (
                                                            <>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <Button 
                                                                            variant="ghost" 
                                                                            size="icon" 
                                                                            className={`h-7 w-7 ${ext.dnd_enabled ? 'text-orange-500' : ''}`}
                                                                            onClick={(e) => { e.stopPropagation(); handleExtensionAction(ext, 'toggle_dnd'); }}
                                                                        >
                                                                            <Moon className="h-3 w-3" />
                                                                        </Button>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>{ext.dnd_enabled ? 'Disattiva DND' : 'Attiva DND'}</TooltipContent>
                                                                </Tooltip>
                                                                <Tooltip>
                                                                    <TooltipTrigger asChild>
                                                                        <Button 
                                                                            variant="ghost" 
                                                                            size="icon" 
                                                                            className={`h-7 w-7 ${ext.queue_logged_in ? 'text-green-500' : ''}`}
                                                                            onClick={(e) => { e.stopPropagation(); handleExtensionAction(ext, ext.queue_logged_in ? 'queue_logout' : 'queue_login'); }}
                                                                        >
                                                                            {ext.queue_logged_in ? <LogOut className="h-3 w-3" /> : <LogIn className="h-3 w-3" />}
                                                                        </Button>
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>{ext.queue_logged_in ? 'Logout Coda' : 'Login Coda'}</TooltipContent>
                                                                </Tooltip>
                                                            </>
                                                        )}
                                                        {activeCalls.length > 0 && (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Button 
                                                                        variant="ghost" 
                                                                        size="icon" 
                                                                        className="h-7 w-7 text-cyan-500"
                                                                        onClick={() => {
                                                                            setTransferDestination(ext.extension);
                                                                            if (activeCalls[0]) {
                                                                                setTransferDialog({ open: true, call: activeCalls[0] });
                                                                            }
                                                                        }}
                                                                    >
                                                                        <PhoneForwarded className="h-3 w-3" />
                                                                    </Button>
                                                                </TooltipTrigger>
                                                                <TooltipContent>Trasferisci qui</TooltipContent>
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                    );
                                })}
                            </div>
                        )}
                    </ScrollArea>
                </div>
                )}

            </div>

            <AlertDialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog({ open: false, action: 'listen', call: null, title: '', description: '' })}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{confirmDialog.title}</AlertDialogTitle>
                        <AlertDialogDescription>{confirmDialog.description}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmAction}>Conferma</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={switchExtDialog.open} onOpenChange={(open) => !open && setSwitchExtDialog({ open: false, targetExtension: '', targetDisplayName: '' })}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Cambio Interno</AlertDialogTitle>
                        <AlertDialogDescription>
                            {switchExtDialog.targetExtension === userExtension
                              ? `Vuoi tornare al tuo interno ${userExtension}? Verrai disconnesso dall'interno ${sipPhone.activeExtension || ''}.`
                              : `Vuoi effettuare il login sull'interno ${switchExtDialog.targetExtension} (${switchExtDialog.targetDisplayName})? Verrai disconnesso dall'interno ${sipPhone.activeExtension || userExtension}.`
                            }
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annulla</AlertDialogCancel>
                        <AlertDialogAction onClick={async () => {
                            const target = switchExtDialog.targetExtension;
                            const isReturning = target === userExtension;
                            setSwitchExtDialog({ open: false, targetExtension: '', targetDisplayName: '' });
                            const switched = await sipPhone.switchExtension(target);
                            if (switched) {
                                sipPhone.openPhone();
                                showFeedback('success', isReturning ? `Tornato all'interno ${target}` : `Passato all'interno ${target}`);
                            } else {
                                showFeedback('error', isReturning ? `Errore nel ritorno all'interno ${target}` : `Errore nel passaggio all'interno ${target}`);
                            }
                        }}>Conferma</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <Dialog open={transferDialog.open} onOpenChange={(open) => !open && setTransferDialog({ open: false, call: null })}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Trasferisci Chiamata</DialogTitle>
                        <DialogDescription>
                            Inserisci il numero o l'estensione di destinazione
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        <Input
                            placeholder="Numero o estensione..."
                            value={transferDestination}
                            onChange={(e) => setTransferDestination(e.target.value)}
                            className="font-mono"
                        />
                        <div className="mt-4 grid grid-cols-4 gap-2 max-h-48 overflow-y-auto">
                            {extensions.filter(e => e.is_registered && e.status === 'available').map(ext => (
                                <Button
                                    key={ext.extension}
                                    variant="outline"
                                    size="sm"
                                    className="text-xs"
                                    onClick={() => setTransferDestination(ext.extension)}
                                >
                                    {ext.extension}
                                </Button>
                            ))}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setTransferDialog({ open: false, call: null })}>
                            Annulla
                        </Button>
                        <Button onClick={executeTransfer} disabled={!transferDestination}>
                            <PhoneForwarded className="h-4 w-4 mr-2" />
                            Trasferisci
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={sipCredentialsDialog.open} onOpenChange={(open) => !open && closeSipCredentialsDialog()}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Key className="h-5 w-5" />
                            Credenziali SIP - {sipCredentialsDialog.extension?.extension}
                        </DialogTitle>
                        <DialogDescription>
                            Configura il tuo softphone o telefono IP con queste credenziali
                        </DialogDescription>
                    </DialogHeader>
                    
                    {sipLoading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                    ) : sipCredentials ? (
                        <div className="space-y-4">
                            <div className="space-y-3">
                                <div className="space-y-1.5">
                                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Username SIP</label>
                                    <div className="flex items-center gap-2">
                                        <Input 
                                            readOnly 
                                            value={sipCredentials.sip_username} 
                                            className="font-mono bg-muted/50"
                                        />
                                        <Button 
                                            variant="outline" 
                                            size="icon"
                                            onClick={() => copyToClipboard(sipCredentials.sip_username, 'Username')}
                                        >
                                            <Copy className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                                
                                <div className="space-y-1.5">
                                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Password SIP</label>
                                    <div className="flex items-center gap-2">
                                        <div className="relative flex-1">
                                            <Input 
                                                readOnly 
                                                type={showPassword ? 'text' : 'password'}
                                                value={sipCredentials.sip_password} 
                                                className="font-mono bg-muted/50 pr-10"
                                            />
                                            <button
                                                type="button"
                                                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                            </button>
                                        </div>
                                        <Button 
                                            variant="outline" 
                                            size="icon"
                                            onClick={() => copyToClipboard(sipCredentials.sip_password, 'Password')}
                                        >
                                            <Copy className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                                
                                <div className="space-y-1.5">
                                    <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Display Name</label>
                                    <Input 
                                        readOnly 
                                        value={sipCredentials.display_name} 
                                        className="bg-muted/50"
                                    />
                                </div>
                            </div>

                            {isAdmin && (
                                <div className="pt-2 border-t">
                                    <Button 
                                        variant="outline" 
                                        size="sm"
                                        className="w-full"
                                        onClick={regenerateSipPassword}
                                        disabled={regeneratingPassword}
                                    >
                                        {regeneratingPassword ? (
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <RefreshCcw className="h-4 w-4 mr-2" />
                                        )}
                                        Rigenera Password
                                    </Button>
                                </div>
                            )}

                            <Separator />

                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <label className="text-sm font-medium flex items-center gap-2">
                                        <Smartphone className="h-4 w-4" />
                                        Dispositivi Registrati
                                    </label>
                                    <Badge variant="secondary">{sipDevices.length}</Badge>
                                </div>
                                
                                {sipDevices.length > 0 ? (
                                    <div className="space-y-2 max-h-40 overflow-y-auto">
                                        {sipDevices.map((device, idx) => (
                                            <div key={idx} className="text-xs bg-muted/50 rounded-lg p-2.5 space-y-1">
                                                <div className="flex items-center justify-between">
                                                    <span className="font-mono font-medium">{device.network_ip}</span>
                                                    <Badge variant="outline" className="text-[10px]">
                                                        {device.expires_in > 0 ? `${Math.floor(device.expires_in / 60)}m` : 'Expired'}
                                                    </Badge>
                                                </div>
                                                <p className="text-muted-foreground truncate">{device.user_agent}</p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-xs text-muted-foreground text-center py-4">
                                        Nessun dispositivo registrato
                                    </p>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-8 text-muted-foreground">
                            Impossibile caricare le credenziali
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={closeSipCredentialsDialog}>
                            Chiudi
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

        </div>
        </TooltipProvider>
    );
}
